import { Button, Grid, LinearProgress, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { AssociatedFilesEdit } from "../../components/associatedFiles/AssociatedFilesEdit";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import { copyToClipboard } from "../../misc/copyToClipboard";
import PackageCustomers from "./PackageCustomers";
import type { Package } from "./PackageEdit";
import PackageEdit from "./PackageEdit";
import type { PackageFile, PackageResponse, UpdatePackageRequest } from "./packagesApi";
import { toPackageTagRequest } from "./packageTagRequest";
import { usePackageUserFriendlyId } from "./packageUserFriendlyId";
import { usePackage } from "./usePackage";

interface Props {
  readonly existingPackage: PackageResponse;
  readonly onUpdatePackage: (request: UpdatePackageRequest) => Promise<PackageResponse>;
  readonly onDeletePackage: () => Promise<void>;
  readonly onAddAttachment: (formData: FormData) => Promise<PackageFile>;
  readonly onDeleteAttachment: (name: string) => Promise<PackageFile[]>;
}

const EditPackagePage: FC<Props> = ({
  existingPackage,
  onUpdatePackage,
  onDeletePackage,
  onAddAttachment,
  onDeleteAttachment,
}) => {
  const navigate = useNavigate();
  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const { control, handleSubmit, watch, setValue } = useForm<Package>({
    defaultValues: existingPackage
      ? {
          ...existingPackage,
          userFriendlyId: existingPackage.id.userFriendlyId!,
          name: existingPackage.name.value,
          wwwName: existingPackage.name.publicValue,
          tags: existingPackage.tags ?? [],
          images: existingPackage.media.images,
          map: existingPackage.media.map,
          regionId: existingPackage.regionId,
          parentPackageId: existingPackage.parentPackageId,
          isPublic: existingPackage.isPublic ?? false,
          isPublicDayDesc: existingPackage.isPublicDayDesc ?? {},
        }
      : undefined,
    mode: "onBlur",
  });

  const regionId = watch("regionId");
  const parentPackageId = watch("parentPackageId");

  const previousValues = useRef({
    regionId,
    parentPackageId,
  });

  const {
    prefix,
    validate: validateId,
    isLoading: isValidationLoading,
    nextUserFriendlyId,
  } = usePackageUserFriendlyId(parentPackageId, regionId, existingPackage.id.id);

  useEffect(() => {
    // Nie ustawiaj ID, zanim dane potrzebne do generowania się załadują.
    if (isValidationLoading) {
      return;
    }

    const regionChanged = previousValues.current.regionId !== regionId;
    const parentChanged = previousValues.current.parentPackageId !== parentPackageId;

    if (!regionChanged && !parentChanged) {
      return;
    }

    previousValues.current = {
      regionId,
      parentPackageId,
    };

    // Przelicz ID po zmianie rodzica/regionu.
    setValue("userFriendlyId", nextUserFriendlyId, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [parentPackageId, regionId, nextUserFriendlyId, isValidationLoading, setValue]);
  useEffect(() => {
    const subscription = watch(() => setErrors((s) => (s.length === 0 ? s : [])));
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    const subscription = watch(() => setErrors((s) => (s.length === 0 ? s : [])));
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleDelete = async () => {
    await onDeletePackage();
    navigate("/admin/packages");
  };

  const handleDuplicate = () => {
    navigate(`/admin/packages/new?from=${existingPackage.id.id}`);
  };

  const submitPackage = async (state: Package) => {
    await onUpdatePackage({
      userFriendlyId: state.userFriendlyId,
      name: { value: state.name, publicValue: state.wwwName ? state.wwwName : undefined },
      currency: state.currency,
      margin: state.margin,
      note: state.note || undefined,
      description: state.description || undefined,
      detailsDict: state.detailsDict,
      days: state.days.map((d) => ({
        dayNumber: d.dayNumber,
        title: d.title,
        details: d.details,
        products: d.products.map((p) => ({
          id: p.id,
          optional: p.optional,
          options: p.options.filter((o): o is string => o !== null),
        })),
        image: d.image,
      })),
      media: {
        images: state.images,
        map: state.map,
      },
      tags: toPackageTagRequest(state.tags),
      isPublic: state.isPublic,
      isPublicDayDesc: state.isPublicDayDesc,
      regionId: state.regionId,
      parentPackageId: state.parentPackageId,
    });
  };

  const handleDeleteAttachment = async (idx: number) => {
    const { name } = existingPackage.files[idx];
    await onDeleteAttachment(name);
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 1,
        marginBottom: 1,
        paddingLeft: 1,
        paddingRight: 1,
      }}
    >
      <Grid container size={12}>
        <PackageEdit
          userFriendlyIdPrefix={prefix}
          validateId={validateId}
          control={control}
          errors={errors}
          parentPackageId={existingPackage.id.id}
          attachments={
            <AssociatedFilesEdit
              values={existingPackage.files}
              maxFiles={1}
              onNewFile={async (f) => {
                await onAddAttachment(f);
              }}
              onRemoveFile={handleDeleteAttachment}
            />
          }
        />
        <Grid
          size={12}
          container
          sx={{
            justifyContent: "flex-end",
          }}
        >
          <Stack direction="row" spacing={2}>
            <CancelButton onClick={() => navigate("/admin/packages")} />
            <Button variant="outlined" color="error" onClick={() => setConfirmationDialogVisible(true)}>
              Usuń
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleDuplicate}>
              Duplikuj
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() =>
                copyToClipboard(`${window.location.host}/calculations/new?fromPackage=${existingPackage.id.id}`)
              }
            >
              Kopiuj link do kalkulacji
            </Button>
            <AsyncLoadingButton onClick={handleSubmit(submitPackage)}>Zapisz</AsyncLoadingButton>
            <ConfirmationDialog
              open={confirmationDialogVisible}
              title="Usuń pakiet"
              content="Czy chcesz usunąć pakiet? Tej operacji nie będzie można już cofnąć."
              onCancel={() => setConfirmationDialogVisible(false)}
              onConfirm={handleDelete}
            />
          </Stack>
        </Grid>
      </Grid>
      <Grid
        container
        size={12}
        sx={{
          paddingBottom: 2,
        }}
      >
        <PackageCustomers packageId={existingPackage.id.id} />
      </Grid>
    </Grid>
  );
};

const InitializedEditPackagePage = () => {
  const {
    package: existingPackage,
    isLoading,
    updatePackage,
    deletePackage,
    addAttachment,
    deleteAttachment,
  } = usePackage();

  if (isLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }

  if (!existingPackage) {
    return (
      <Grid
        container
        spacing={2}
        sx={{
          marginTop: 1,
          marginBottom: 1,
        }}
      >
        <Typography
          sx={{
            margin: 2,
          }}
        >
          Taki pakiet nie istnieje
        </Typography>
      </Grid>
    );
  }

  return (
    <EditPackagePage
      existingPackage={existingPackage}
      onUpdatePackage={updatePackage}
      onDeletePackage={() => deletePackage(existingPackage.id.id)}
      onAddAttachment={addAttachment}
      onDeleteAttachment={deleteAttachment}
    />
  );
};

export default InitializedEditPackagePage;
